import { Reikwijdte, Traject } from "@/entities/dossier-details";
import { TVVBestelStatus } from "@/entities/tvv";
import { VestigingStatus } from "@/entities/vestiging/constants.ts";
import { VosIconVariant } from "@/features/dossier-balk";
import { DossierOverzichtItemDto } from "@/shared/types";
import { compareDesc } from "date-fns";
import { BusEvacuatieStatus } from "../betrokken-treinen";
import { VosStationStatus } from "../medewerkers-op-station";

function valueIncludesQuery(value: unknown, query: string): boolean {
    if (typeof value === "string" || typeof value === "number") {
        return String(value).toLowerCase().includes(query);
    }

    if (Array.isArray(value)) {
        return value.some((item) => valueIncludesQuery(item, query));
    }

    if (value && typeof value === "object") {
        return Object.values(value).some((v) => valueIncludesQuery(v, query));
    }

    return false;
}

export type DossierOverzicht = {
    dossierId: number;
    spoorwebNummer?: string;
    reikwijdte: Reikwijdte;
    trajecten: Traject[];
    stations: string[];
    startDatumTijd: Date;
    omschrijving: string;
    omschrijvingExtraContext?: string;
    uitgezetteDiensten: UitgezetteDienst[];
    acties: number;
    suggesties: number;
    betrokkenTreinen: string[];
};

export const DossierOverzicht = {
    fromDto: (dto: DossierOverzichtItemDto): DossierOverzicht => {
        return {
            dossierId: dto.dossierId,
            spoorwebNummer: dto.spoorwebNummer,
            reikwijdte: dto.reikwijdte,
            trajecten: dto.trajecten.map((traject) => Traject.fromDto(traject)),
            stations: dto.stations,
            startDatumTijd: new Date(dto.startDatumTijd),
            omschrijving: dto.omschrijving,
            omschrijvingExtraContext: dto.omschrijvingExtraContext,
            uitgezetteDiensten: UitgezetteDienst.fromOverzicht(dto),
            acties: dto.acties,
            suggesties: dto.suggesties,
            betrokkenTreinen: dto.betrokkenTreinen,
        };
    },
    copy: (dossier: DossierOverzicht, extra: Partial<DossierOverzicht>): DossierOverzicht => ({
        ...dossier,
        ...extra,
    }),
    sort: (a: DossierOverzicht, b: DossierOverzicht) =>
        compareDesc(a.startDatumTijd, b.startDatumTijd) || String(a.dossierId).localeCompare(String(b.dossierId)),
    createAfgerondFilterFn: (query: string) => {
        const trimmed = query.trim().toLowerCase();
        if (trimmed === "") {
            return () => true;
        }

        const terms = trimmed.split(/\s+/);

        const filterProperties: (keyof DossierOverzicht)[] = [
            "dossierId",
            "spoorwebNummer",
            "reikwijdte",
            "trajecten",
            "stations",
            "omschrijving",
            "omschrijvingExtraContext",
            "betrokkenTreinen",
        ];

        return (dossier: DossierOverzicht) =>
            terms.every((term) => filterProperties.some((prop) => valueIncludesQuery(dossier[prop], term)));
    },
};

export type KoffieDienstStatus = "Aangevraagd" | "Besteld" | "Actief" | "StopAangevraagd" | "GaatNietLeveren" | "Gestopt";

export type KoffieDienst = {
    type: "koffie";
    variant: KoffieDienstStatus;
    countByStatus: Record<VestigingStatus, number>;
    landelijkeFaciliteiten: VestigingStatus | undefined;
};

export type TvvDienst = {
    type: "tvv";
    variant: TVVBestelStatus;
    countByStatus: Record<TVVBestelStatus, number>;
};

export type BusEvacuatieDienst = {
    type: "busEvacuatie";
    variant: BusEvacuatieStatus;
    countByStatus: Record<BusEvacuatieStatus, number>;
};

type VosAanvraagStatus = Exclude<VosStationStatus, "NietAangevraagd">;

export type VosDienst = {
    type: "vos";
    variant: VosIconVariant;
    countByStatus: Record<VosAanvraagStatus, number>;
};

export type UitgezetteDienst = KoffieDienst | TvvDienst | BusEvacuatieDienst | VosDienst;

type DienstVariant<T extends string, Type extends string> = { type: Type; variant: T; countByStatus: Record<T, number> };

export const UitgezetteDienst = {
    priorities: {
        koffie: [
            VestigingStatus.BESTELD,
            VestigingStatus.STOP_AANGEVRAAGD,
            VestigingStatus.ACTIEF,
            VestigingStatus.GESTOPT,
            VestigingStatus.GAAT_NIET_LEVEREN,
        ],
        tvv: [
            TVVBestelStatus.Aangevraagd,
            TVVBestelStatus.Gedeeltelijk,
            TVVBestelStatus.StopAangevraagd,
            TVVBestelStatus.Volledig,
            TVVBestelStatus.Gestopt,
        ],
        busEvacuatie: ["Aanvraag", "Gedeeltelijk", "AnnuleringAangevraagd", "Volledig", "Afgerond", "Geannuleerd"],
        vos: ["AanvraagLoopt", "VosIngezet", "GeenVosInzetbaar", "Gestopt"],
    } satisfies Record<UitgezetteDienst["type"], string[]>,
    fromOverzicht: (overzicht: DossierOverzichtItemDto): UitgezetteDienst[] => {
        const uitgezetteDiensten: UitgezetteDienst[] = [];
        const koffieDienst = UitgezetteDienst.fromFaciliteiten(overzicht.faciliteiten, overzicht.landelijkeFaciliteiten);
        const tvvDienst = UitgezetteDienst.fromTvv(overzicht.tvv);
        const busEvacuatieDienst = UitgezetteDienst.fromBusEvacuatie(overzicht.busEvacuaties);
        const vosDienst = UitgezetteDienst.fromVosAanvragen(overzicht.vosAanvragen);

        if (koffieDienst) {
            uitgezetteDiensten.push(koffieDienst);
        }

        if (tvvDienst) {
            uitgezetteDiensten.push(tvvDienst);
        }

        if (busEvacuatieDienst) {
            uitgezetteDiensten.push(busEvacuatieDienst);
        }

        if (vosDienst) {
            uitgezetteDiensten.push(vosDienst);
        }

        return uitgezetteDiensten;
    },

    fromFaciliteiten: (
        faciliteiten: DossierOverzichtItemDto["faciliteiten"],
        landelijkeFaciliteiten: VestigingStatus | undefined
    ): KoffieDienst | null => {
        const countByStatus = faciliteiten as Record<VestigingStatus, number>;
        // copy for calculating variant, counts landelijkeFaciliteiten status as "vestigingStatus"
        const totals = { ...countByStatus } as Record<VestigingStatus, number>;
        if (landelijkeFaciliteiten) {
            totals[landelijkeFaciliteiten] = (totals[landelijkeFaciliteiten] ?? 0) + 1;
        }

        const total = Object.values(totals).reduce((sum, n) => sum + n, 0);

        // Edge case: everything is BESTELD, no response yet
        if (total > 0 && totals[VestigingStatus.BESTELD] === total) {
            return { type: "koffie", variant: "Aangevraagd", countByStatus, landelijkeFaciliteiten }; // GRIJS
        }

        return UitgezetteDienst.getDienstVariant("koffie", totals, (dienst) => ({
            ...dienst,
            countByStatus,
            landelijkeFaciliteiten: landelijkeFaciliteiten,
        }));
    },

    fromTvv: (tvv: DossierOverzichtItemDto["tvv"]): TvvDienst | null => {
        const tvvStatuses = tvv as Record<TVVBestelStatus, number>;
        return UitgezetteDienst.getDienstVariant("tvv", tvvStatuses);
    },

    fromBusEvacuatie: (busEvacuaties: DossierOverzichtItemDto["busEvacuaties"]): BusEvacuatieDienst | null => {
        const busEvacuatieStatuses = busEvacuaties as Record<BusEvacuatieStatus, number>;
        return UitgezetteDienst.getDienstVariant("busEvacuatie", busEvacuatieStatuses);
    },

    fromVosAanvragen: (vosAanvragen: DossierOverzichtItemDto["vosAanvragen"]): VosDienst | null => {
        const vosStatuses = vosAanvragen as Record<VosAanvraagStatus, number>;

        const variantMap: Record<VosAanvraagStatus, VosIconVariant> = {
            AanvraagLoopt: "InBehandeling",
            VosIngezet: "Actief",
            GeenVosInzetbaar: "Afgerond",
            Gestopt: "Afgerond",
        };

        return UitgezetteDienst.getDienstVariant("vos", vosStatuses, (vosDienst): VosDienst => {
            return {
                ...vosDienst,
                variant: variantMap[vosDienst.variant],
            };
        });
    },
    getDienstVariant<T extends string, Type extends UitgezetteDienst["type"], R = DienstVariant<T, Type>>(
        type: Type,
        countByStatus: Record<T, number>,
        extend?: (dienst: DienstVariant<T, Type>) => R
    ): R | null {
        const extendFn = extend ? extend : (dienst: DienstVariant<T, Type>) => dienst as R;
        const prioritiesList = UitgezetteDienst.priorities[type] as T[];
        const highestPriority = prioritiesList.find((status) => countByStatus[status] > 0);
        if (highestPriority) {
            return extendFn({ type, variant: highestPriority, countByStatus });
        }
        return null;
    },
};
